// auth.ts — Supabase-backed identity + entitlement for the app.
//
// Replaces the old Thinkific OAuth + its trial (the free trial is now TRIAL_DAYS
// = 10 days, below — do not quote any other figure). The app is now ONLY an
// identity client: it signs the user in (Apple/Google/email/OTP) and reads
// their entitlement (`free` | `member`). Sales happen on the website; the
// Stripe webhook flips entitlement server-side. Fails soft: when Supabase
// isn't configured, the app runs ungated.
import { Browser } from '@capacitor/browser';
import { supabase } from './supabaseClient';
import { SUPABASE, PURCHASE_URL, SHOW_PURCHASE } from '@/config/supabase';
import { KIT_SYNC_URL } from '@/config/backend';
import { Capacitor } from '@capacitor/core';
import { storage } from './storage';

export type Entitlement = 'free' | 'member';
export type Access = 'signedout' | 'free' | 'member';

export interface AuthUser {
  userId: string;
  email?: string;
  fullName?: string | null;
  // First name / nickname collected once at account creation (profiles.first_name).
  // Used to personalise the app and email (Kit). Null for legacy accounts created
  // before this was collected — the UI degrades gracefully (no greeting).
  firstName?: string | null;
  // Who the account-holder is (profiles.user_type), chosen once in onboarding and
  // LOCKED. Read here so the onboarding step is asked once and never again — the app
  // hydrates local state from this on sign-in. null for accounts not yet asked.
  userType?: 'survivor' | 'curious' | 'staff' | 'other' | null;
}
export interface AuthSession {
  user: AuthUser;
  entitlement: Entitlement;
  expiresAt: number;
  // Epoch ms the account was created (Supabase user.created_at). This is the
  // free-trial anchor — "sign in, 10 days counting" — so the window survives
  // re-installs and is the same on every device. Optional: absent on older
  // cached sessions (the trial helpers fail open in that case).
  createdAt?: number;
  // The Supabase access token (JWT), carried so the progress/research sync layer
  // (services/sync.ts) can authenticate to the backend. Optional: entitlement
  // logic never reads it, and a session can exist before it's populated.
  accessToken?: string;
  // Where the current membership came from (profiles.entitlement_source), e.g.
  // 'stripe' | 'apple' | 'google' | 'org' | 'manual'. Display/management only;
  // never part of the member decision.
  entitlementSource?: string | null;
  // Billing plan + paid-through date (profiles.plan / current_period_end), for the
  // cancel-subscription UI only. Never part of the member decision.
  plan?: 'monthly' | 'annual' | null;
  currentPeriodEnd?: string | null;
}

/** Entitlement for a session, failing closed to `free` for a null session. */
export function entitlementOf(s: AuthSession | null): Entitlement {
  return s?.entitlement ?? 'free';
}

/** Grace window in which a just-verified account still counts as brand new. Generous
 *  on purpose: it has to absorb device-clock skew (createdAt is the SERVER's clock,
 *  Date.now() is the phone's) plus however long it took to fetch the code from email. */
export const NEW_ACCOUNT_GRACE_MS = 10 * 60 * 1000;

/**
 * True when this session belongs to an account that ALREADY EXISTED before this
 * sign-in, so someone who tapped "Create my account" with a registered email must be
 * routed to the returning-user flow instead of being walked through first-run again.
 *
 * Deliberately decided AFTER the code verifies, never before. Asking the server
 * "is this email registered?" from the sign-in form would let anyone enumerate our
 * users; for this population, confirming that a given person has an account here is
 * exactly the disclosure we must not make. Waiting until the code is verified means
 * only the mailbox owner ever learns the answer.
 *
 * Two signals, in order of certainty:
 *   • user_type is set — they completed the onboarding fork on an earlier run;
 *   • the account predates the grace window — created on an earlier visit.
 *
 * Fails toward "new": a misread only re-shows a warm welcome screen, whereas wrongly
 * calling a new account "returning" would skip it.
 */
export function isReturningAccount(s: AuthSession | null, nowMs: number = Date.now()): boolean {
  if (!s) return false;
  if (s.user.userType) return true;
  return s.createdAt != null && nowMs - s.createdAt > NEW_ACCOUNT_GRACE_MS;
}

/** Access decision: signedout / free / member. */
export function accessFromSession(s: AuthSession | null): Access {
  if (!s) return 'signedout';
  return s.entitlement === 'member' ? 'member' : 'free';
}

// ─────────────────────────── free trial ───────────────────────────
// A signed-in non-member gets full access for TRIAL_DAYS from account creation;
// after that the main programme falls back to the free Sign-Up cap. (Calm Your
// Body and the self-compassion course are NEVER gated, so they stay free during
// and after the trial — the "free gift".) Server entitlement always wins: a
// 'member' is never on a trial clock.
export const TRIAL_DAYS = 10;
const DAY_MS = 86_400_000;

/** Epoch ms the trial ends (account creation + TRIAL_DAYS), or null if the
 *  creation date is unknown (e.g. an older cached session). */
export function trialEndsAt(s: AuthSession | null): number | null {
  return s?.createdAt != null ? s.createdAt + TRIAL_DAYS * DAY_MS : null;
}

/** True while a signed-in non-member is still inside the trial window. Members
 *  are never "on trial". Fails OPEN on an unknown creation date so a transient
 *  data gap never wrongly caps a signed-in user. */
export function trialActive(s: AuthSession | null, now: number = Date.now()): boolean {
  if (!s || s.entitlement === 'member') return false;
  const ends = trialEndsAt(s);
  return ends == null ? true : now < ends;
}

/** Whole days left in the trial (0 once expired), or null if unknown / member. */
export function trialDaysLeft(s: AuthSession | null, now: number = Date.now()): number | null {
  if (!s || s.entitlement === 'member') return null;
  const ends = trialEndsAt(s);
  return ends == null ? null : Math.max(0, Math.ceil((ends - now) / DAY_MS));
}

// Web redirect resolved lazily so importing this module never touches `window`
// (it doesn't exist under the node test env / SSR — only inside a browser flow).
// Used only for the web magic-link return; native uses the in-app OTP code.
const webRedirect = (): string => (typeof window !== 'undefined' ? window.location.origin : '');

// Last-known entitlement, so an offline re-check can fall back to it instead of
// demoting a paying member, and so we can throttle re-checks (see lastCheckedAt).
const ENT_CACHE_KEY = 'renurture.auth.entitlement.v1';
interface EntCache { entitlement: Entitlement; checkedAt: number; }
const readEntCache = (): EntCache | null => storage.getJSON<EntCache | null>(ENT_CACHE_KEY, null);

// Last good SESSION (user + token), so the app stays ENTERABLE offline / on weak
// wifi. supabase-js can't always restore a session without the network (an expired
// JWT needs a refresh round-trip), and a null session would otherwise drop the user
// to the hard LoginWall — locking a paying member out of even the calming tools.
// We cache the user here on every successful build and fall back to it when the
// live read fails. Cleared ONLY on explicit sign-out, so a never-signed-in /
// signed-out user still correctly hits the wall.
const SESSION_CACHE_KEY = 'renurture.auth.session.v1';
interface SessionCache { user: AuthUser; entitlement: Entitlement; expiresAt: number; createdAt?: number; accessToken?: string; entitlementSource?: string | null; plan?: 'monthly' | 'annual' | null; currentPeriodEnd?: string | null; cachedAt: number; }
const readSessionCache = (): SessionCache | null => storage.getJSON<SessionCache | null>(SESSION_CACHE_KEY, null);

// OFFLINE GRACE: how long an on-device session is trusted WITHOUT a successful
// online re-verify. A signed-in user who loses wifi keeps Calm Your Body + the
// toolkits for this long (the safety tools they most need offline), then must
// reconnect. Online users re-verify continuously (buildSession refreshes the live
// session + entitlement and bumps cachedAt), so this only ever bites a device that
// has genuinely been offline the whole window.
const OFFLINE_GRACE_MS = 7 * 86_400_000;
// True when the cached session is still within the offline-grace window. Caches
// written before cachedAt existed are treated as fresh (don't lock out on upgrade).
const sessionCacheFresh = (c: SessionCache | null): c is SessionCache =>
  !!c && (!c.cachedAt || Date.now() - c.cachedAt <= OFFLINE_GRACE_MS);

// navigator.onLine is reliable inside the Capacitor WKWebView/Android WebView and
// needs no native plugin: a definite `false` means no connectivity right now.
const isOffline = (): boolean => typeof navigator !== 'undefined' && navigator.onLine === false;

/** Synchronous last-known session from on-device cache (NO network). Lets the app
 *  render INSTANTLY on launch from the previous good session; the live session +
 *  entitlement are then fetched in the background and swapped in. Null if the user
 *  has never signed in or has signed out (cache is cleared on sign-out). */
export function cachedSession(): AuthSession | null {
  const cached = readSessionCache();
  // Beyond the 7-day offline grace the cache is no longer trusted on its own. Online
  // users are unaffected: this is only the instant-paint seed, and buildSession will
  // refresh a live session regardless. An offline device past the window drops to the
  // sign-in wall (cache is retained, so a reconnect can still restore it).
  if (!sessionCacheFresh(cached)) return null;
  return {
    user: cached.user,
    entitlement: readEntCache()?.entitlement ?? cached.entitlement,
    expiresAt: cached.expiresAt,
    createdAt: cached.createdAt,
    accessToken: cached.accessToken,
    entitlementSource: cached.entitlementSource,
    plan: cached.plan,
    currentPeriodEnd: cached.currentPeriodEnd,
  };
}

/** Epoch ms of the last SUCCESSFUL entitlement read (0 if never). Used to cap
 *  foreground re-checks to once per day. */
export function lastCheckedAt(): number {
  return readEntCache()?.checkedAt ?? 0;
}

// Cap the profile read so a no-network device can't hang the app; on timeout the
// caller keeps the last-known entitlement.
const READ_TIMEOUT_MS = 8000;
function withTimeout<T>(p: PromiseLike<T>, ms: number): Promise<T> {
  return Promise.race([
    Promise.resolve(p),
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ]);
}

// SIGN-OUT EPOCH. A buildSession() is two network reads long (restore, then the
// profile), and one is nearly always in flight: the launch read, the foreground
// re-check, and every auth event start one. If the user taps "Sign out" while one
// is running, it lands AFTERWARDS holding a session that was valid when it started —
// and, unguarded, it re-writes the session/entitlement caches this sign-out just
// cleared and hands the session back to useAuth, which puts the user straight back
// into the app still signed in. (Tapping sign out again then worked, because by then
// nothing was in flight.) Every read captures the epoch it began in and discards its
// result if a sign-out has happened since.
let signOutEpoch = 0;

/** Map a supabase user + entitlement row into our AuthSession shape. */
async function buildSession(): Promise<AuthSession | null> {
  const sb = supabase();
  if (!sb) return null;
  const epoch = signOutEpoch;
  // True once a sign-out has happened since this read began: the answer is stale and
  // must neither be cached nor returned.
  const signedOutMeanwhile = () => epoch !== signOutEpoch;
  // Bound the session RESTORE too (not just the profile read): supabase-js can
  // hang refreshing an expired token on a poor connection, which would otherwise
  // leave the app stuck on "Checking your membership…". On timeout/failure we
  // treat it as signed out so the app always proceeds (to LoginWall).
  let session;
  try {
    const res = await withTimeout(sb.auth.getSession(), READ_TIMEOUT_MS);
    session = res.data.session;
  } catch {
    session = null; // timeout / offline — fall through to the cached session below
  }
  if (signedOutMeanwhile()) return null;
  // Offline-resilient entry: if supabase can't give us a live session right now
  // (slow/offline/unrefreshable token) but the user HAS signed in on this device,
  // keep them in with the cached user + last-known entitlement. Only a user who has
  // never signed in or who signed out (cache cleared) falls through to null → wall.
  if (!session?.user) {
    const cached = readSessionCache();
    // Keep a signed-in user in with their last-known session + entitlement while
    // within the 7-day offline grace; past that, require a reconnect.
    if (sessionCacheFresh(cached)) {
      return {
        user: cached.user,
        entitlement: readEntCache()?.entitlement ?? cached.entitlement,
        expiresAt: cached.expiresAt,
        createdAt: cached.createdAt,
        accessToken: cached.accessToken,
        entitlementSource: cached.entitlementSource,
        plan: cached.plan,
        currentPeriodEnd: cached.currentPeriodEnd,
      };
    }
    return null;
  }
  const u = session.user;
  // Member iff profiles says 'member' AND the paid period hasn't lapsed.
  // profiles.entitlement is written ONLY by the service-role Stripe webhook
  // (clients read their own row via RLS but never self-grant). Expiry is judged
  // CLIENT-side and re-checked on foreground, so no server cron is needed: an
  // annual one-time seat has current_period_end = purchase + 365d and simply
  // stops being valid; a monthly seat keeps it pushed forward by the renewal
  // webhook (a 3-day grace absorbs renewal lag); a cancellation flips entitlement
  // to 'free' at once; null period = lifetime / manual / B2B grant.
  // On a network failure we KEEP the last-known entitlement — never demote a
  // paying member just because their wifi dropped.
  // Generous grace so transient/extended offline never demotes a paying member
  // (industry norm for wellbeing apps is ~7-30 days; we use 30 for this cohort).
  const GRACE_MS = 30 * 86_400_000;
  let entitlement: Entitlement = readEntCache()?.entitlement ?? 'free';
  // First name from the canonical profiles row (falls back to signup metadata below).
  let firstName: string | null = null;
  let userType: AuthUser['userType'] = null;
  let entitlementSource: string | null = readSessionCache()?.entitlementSource ?? null;
  let plan: 'monthly' | 'annual' | null = readSessionCache()?.plan ?? null;
  let currentPeriodEnd: string | null = readSessionCache()?.currentPeriodEnd ?? null;
  try {
    // maybeSingle (not single): 0 rows comes back as data=null WITHOUT an error, so we
    // can tell "account deleted" (reached the server, no row) apart from a network error.
    const { data, error } = await withTimeout(
      sb.from('profiles').select('entitlement, entitlement_source, plan, current_period_end, status, first_name, user_type').eq('id', u.id).maybeSingle(),
      READ_TIMEOUT_MS,
    );
    if (signedOutMeanwhile()) return null; // don't re-cache an entitlement we no longer hold
    if (error) throw error;
    if (!data) {
      // We REACHED the server but this user has no profile row: the account has been
      // deleted (or disabled) server-side. The local JWT may still be cryptographically
      // valid for up to an hour, which is why the app was loading a phantom free user.
      // Tear the session down so the app drops to the sign-in wall instead.
      storage.remove(ENT_CACHE_KEY);
      storage.remove(SESSION_CACHE_KEY);
      try { await sb.auth.signOut(); } catch { /* best-effort */ }
      return null;
    }
    const periodEnd = data?.current_period_end as string | null | undefined;
    const notExpired = !periodEnd || Date.parse(periodEnd) > Date.now() - GRACE_MS;
    // An admin can SUSPEND an account (participant console) — access is removed
    // without deleting it. A suspended user is treated as free regardless of any
    // membership. `status` may be absent until migration 0005 is applied (older
    // rows / pre-migration) — default to active so nothing breaks.
    const suspended = (data as { status?: string } | null)?.status === 'suspended';
    entitlement = data?.entitlement === 'member' && notExpired && !suspended ? 'member' : 'free';
    firstName = ((data as { first_name?: string | null } | null)?.first_name as string) ?? null;
    userType = ((data as { user_type?: AuthUser['userType'] } | null)?.user_type) ?? null;
    entitlementSource = ((data as { entitlement_source?: string | null } | null)?.entitlement_source) ?? null;
    const rawPlan = (data as { plan?: string | null }).plan;
    plan = rawPlan === 'monthly' || rawPlan === 'annual' ? rawPlan : null;
    currentPeriodEnd = periodEnd ?? null;
    storage.setJSON(ENT_CACHE_KEY, { entitlement, checkedAt: Date.now() } as EntCache);
  } catch {
    /* keep last-known entitlement (offline / slow); never demote a member */
  }
  if (signedOutMeanwhile()) return null;
  const built: AuthSession = {
    user: {
      userId: u.id,
      email: u.email ?? undefined,
      fullName: (u.user_metadata?.full_name as string) ?? null,
      firstName: firstName ?? (u.user_metadata?.first_name as string) ?? null,
      userType,
    },
    entitlement,
    expiresAt: (session.expires_at ?? 0) * 1000,
    // Trial anchor: when this account was created (Supabase user.created_at).
    createdAt: u.created_at ? Date.parse(u.created_at) : undefined,
    // Carried for the progress/research sync layer (services/sync.ts).
    accessToken: session.access_token,
    entitlementSource,
    plan,
    currentPeriodEnd,
  };
  // Cache the good session so the app stays enterable offline next launch.
  storage.setJSON(SESSION_CACHE_KEY, { ...built, cachedAt: Date.now() } as SessionCache);
  return built;
}

/** Current persisted session (after supabase-js has restored it), or null.
 *  Never rejects — any failure resolves to null so callers can't hang. */
export async function getSession(): Promise<AuthSession | null> {
  try {
    return await buildSession();
  } catch {
    return null;
  }
}

/** The CURRENT Supabase access token, auto-refreshed by supabase-js if the cached
 *  one has expired. Call this right before a backend request so we never send a
 *  STALE token from the on-device session cache (which would 401). Returns null
 *  when signed out / unconfigured / offline (caller can fall back to what it has). */
export async function freshAccessToken(): Promise<string | null> {
  const sb = supabase();
  if (!sb) return null;
  try {
    const { data } = await withTimeout(sb.auth.getSession(), READ_TIMEOUT_MS);
    return data.session?.access_token ?? null;
  } catch {
    return null;
  }
}

/** Subscribe to auth changes; returns an unsubscribe fn. */
export function onAuthStateChange(cb: (s: AuthSession | null) => void): () => void {
  const sb = supabase();
  if (!sb) return () => {};
  const handle = async (event: string) => {
    // SIGNED_OUT fires on an explicit sign-out, on a token revoke / account deletion,
    // AND (the trap) when supabase-js can't refresh an expired token because the
    // device is OFFLINE. We must NOT tear down a paying member's cached session for a
    // network drop — that was logging people out of Calm Your Body with no wifi.
    //
    //   • OFFLINE  → keep the cache; re-emit the cached session so the user stays in
    //                (within the 7-day grace). The cache is retained either way, so a
    //                reconnect can still restore a genuinely-revoked session to null.
    //   • ONLINE   → genuine: token revoked / account deleted / explicit sign-out
    //                (which already cleared the cache). Wipe and drop to the wall.
    if (event === 'SIGNED_OUT') {
      if (isOffline()) { cb(cachedSession()); return; }
      storage.remove(ENT_CACHE_KEY);
      storage.remove(SESSION_CACHE_KEY);
      cb(null);
      return;
    }
    cb(await buildSession());
  };
  // supabase-js runs this callback while it still HOLDS its internal auth lock, and it
  // waits for the callback to finish before releasing it. buildSession() calls back into
  // the same client (getSession), which then queues behind that very lock: a deadlock,
  // broken only by our own 8s read timeout. That is why a launch sat ~8 seconds before
  // the session appeared, and why a sign-out tapped inside that window always had a read
  // in flight to race with. Hand the work to the next tick so the lock is released first
  // (the documented rule: never call the client from inside this callback).
  const { data } = sb.auth.onAuthStateChange((event) => {
    setTimeout(() => { void handle(event); }, 0);
  });
  return () => data.subscription.unsubscribe();
}

/** Passwordless: email a 6-digit OTP code. A new email is created on first
 *  verify (shouldCreateUser defaults true). On web we also pass a return URL so
 *  the magic link works; native users just type the code. */
export async function requestOtp(email: string, firstName?: string): Promise<{ ok: boolean; error?: string }> {
  const sb = supabase();
  if (!sb) return { ok: false };
  // Carry the first name as signup metadata so the handle_new_user trigger seeds
  // profiles.first_name on a brand-new account. (Ignored by Supabase for an existing
  // user; verifyOtp also writes it explicitly as the canonical path.)
  const fn = firstName?.trim();
  const { error } = await sb.auth.signInWithOtp({
    email: email.trim(),
    options: {
      emailRedirectTo: Capacitor.isNativePlatform() ? undefined : webRedirect(),
      data: fn ? { first_name: fn } : undefined,
    },
  });
  if (error) {
    console.warn('[auth] requestOtp failed', error.message);
    // Supabase enforces a short per-email cooldown between sends (≈60s). Hitting it
    // right after a sign-out/in is normal; surface a calm, specific message rather
    // than the generic "couldn't send" so the user waits instead of hammering it.
    const msg = error.message || '';
    const rateLimited = (error as { status?: number }).status === 429 || /rate|after \d+ second|only request/i.test(msg);
    return {
      ok: false,
      error: rateLimited
        ? 'A code was just sent. Please wait a moment before asking for another, then check your inbox.'
        : 'We couldn’t send the code. Check the email and try again.',
    };
  }
  return { ok: true };
}

/** Verify the 6-digit email OTP.
 *  A brand-new user's first code is a `signup`-type token; a returning user's is
 *  an `email`-type one. The app can't tell which at verify time, so we try `email`
 *  then fall back to `signup`. A wrong-type attempt finds no matching token and so
 *  does NOT consume the real one, which makes the fallback safe. */
/**
 * APP REVIEW SIGN-IN. One fixed address signs in with a PIN instead of an emailed code.
 *
 * WHY IT EXISTS. Sign-in is passwordless: we email a one-time code. An App Store or Play
 * reviewer cannot receive that code, and handing over a mailbox is not an option, so
 * without this the app is unreviewable and gets rejected under Guideline 2.1.
 *
 * WHY THE PIN IS NOT IN THIS FILE. The bundle is public — the web build serves this
 * JavaScript to anyone, and its contents are readable (the developer PIN sits in there in
 * clear text today). So the PIN is NOT compared here. It is the account's Supabase
 * PASSWORD, checked by Supabase, exactly like any password. All that ships in the app is
 * the address, which is not a secret: knowing it gets you a password prompt and nothing
 * else. The PIN itself lives only in Supabase and in the App Store Connect review notes.
 *
 * Housekeeping between submissions: change the password in Supabase to rotate it, and
 * keep the account on an ordinary entitlement so it sees what a reviewer needs to see.
 */
export const REVIEW_EMAIL = 'appreview@renurture.org';

/** True when this address is the review account, so the UI asks for a PIN not a code.
 *  An exact match (after trim and lowercase): the address goes in the App Store Connect
 *  review notes and is typed as written. */
export const isReviewEmail = (email: string): boolean =>
  email.trim().toLowerCase() === REVIEW_EMAIL;

/** Sign in the review account with its PIN. Returns null if the PIN is wrong, so the
 *  caller shows the same calm failure as a bad code. */
export async function signInWithReviewPin(email: string, pin: string): Promise<AuthSession | null> {
  const sb = supabase();
  if (!sb) return null;
  if (!isReviewEmail(email)) return null; // never a password path for a real user's address
  const { error } = await sb.auth.signInWithPassword({ email: REVIEW_EMAIL, password: pin.trim() });
  if (error) { console.warn('[auth] review sign-in failed', error.message); return null; }
  return await getSession();
}

export async function verifyOtp(email: string, token: string, firstName?: string): Promise<AuthSession | null> {
  const sb = supabase();
  if (!sb) return null;
  const e = email.trim();
  const t = token.trim();
  let { error } = await sb.auth.verifyOtp({ email: e, token: t, type: 'email' });
  if (error) {
    ({ error } = await sb.auth.verifyOtp({ email: e, token: t, type: 'signup' }));
  }
  if (error) { console.warn('[auth] verifyOtp failed', error.message); return null; }
  // Canonical first-name write at creation. Best-effort: the signup trigger already
  // seeded it from metadata, and a failure here must never block sign-in. RLS lets a
  // user write their OWN first_name (it isn't one of the locked entitlement columns).
  const fn = firstName?.trim();
  if (fn) {
    try {
      const { data: who } = await sb.auth.getUser();
      const uid = who.user?.id;
      if (uid) await sb.from('profiles').update({ first_name: fn }).eq('id', uid);
    } catch (err) {
      console.warn('[auth] first_name write skipped', (err as Error).message);
    }
  }
  // Sync the new contact (email + first name) into Kit for personalised email, via
  // the Worker (which holds KIT_API_KEY). Fire-and-forget: never block sign-in on it.
  try {
    const { data: sess } = await sb.auth.getSession();
    const tok = sess.session?.access_token;
    if (tok) {
      void fetch(KIT_SYNC_URL, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: fn || '' }),
      }).catch(() => {});
    }
  } catch { /* best-effort */ }
  return buildSession();
}

/** Write the account-holder's type to profiles.user_type (chosen once in onboarding).
 *  This is what the research backend reads to decide who gets a PID, so it must land
 *  on the profile. Set-once is enforced by a DB trigger; RLS lets a user write their
 *  own row. Best-effort with a short retry — onboarding is online, so it should land. */
export async function saveUserType(type: 'survivor' | 'curious' | 'staff' | 'other'): Promise<boolean> {
  const sb = supabase();
  if (!sb) return false;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const { data: who } = await sb.auth.getUser();
      const uid = who.user?.id;
      if (!uid) return false;
      const { error } = await sb.from('profiles').update({ user_type: type }).eq('id', uid);
      if (!error) return true;
    } catch (err) {
      console.warn('[auth] user_type write retry', (err as Error).message);
    }
  }
  return false;
}

export async function signOut(): Promise<void> {
  // Before anything else, so a read already in flight can tell that its answer is
  // out of date and discards it instead of restoring the session we're clearing.
  signOutEpoch += 1;
  storage.remove(ENT_CACHE_KEY);
  storage.remove(SESSION_CACHE_KEY); // so a signed-out user is NOT auto-restored offline
  await supabase()?.auth.signOut();
}

/** Re-read entitlement (call after a web purchase / on foreground). */
export async function recheckEntitlement(): Promise<AuthSession | null> {
  return buildSession();
}

/** Open the website pricing page in the system browser. No-op until configured, and a
 *  no-op on native (App Store guideline 3.1.1 — no steering to external purchase). */
export async function startSignup(): Promise<void> {
  if (SHOW_PURCHASE && PURCHASE_URL) await Browser.open({ url: PURCHASE_URL });
}

void SUPABASE; // referenced for config presence; keep import side-effect-free
